(() => {
  "use strict";

  class RequestQueue {
    constructor(limit) {
      this.limit = limit;
      this.active = 0;
      this.waiting = [];
    }

    run(key, task, priority) {
      const promise = new Promise((resolve, reject) => {
        const entry = { key, task, resolve, reject };
        priority ? this.waiting.unshift(entry) : this.waiting.push(entry);
      });
      this.drain();
      return promise;
    }

    prioritize(key) {
      const position = this.waiting.findIndex(entry => entry.key === key);
      if (position > 0) this.waiting.unshift(this.waiting.splice(position, 1)[0]);
    }

    drain() {
      while (this.active < this.limit && this.waiting.length) {
        const entry = this.waiting.shift();
        this.active += 1;
        Promise.resolve().then(entry.task).then(entry.resolve, entry.reject).finally(() => {
          this.active -= 1;
          this.drain();
        });
      }
    }
  }

  class SearchResults {
    constructor(page) {
      this.page = page;
      this.section = document.getElementById("shopify-section-" + page.dataset.section);
      this.grid = this.section.querySelector(".pagination-grid");
      this.countElement = this.section.querySelector("[data-search-results-count]");
      this.pagination = this.section.querySelector("[data-search-pagination]");
      this.button = this.pagination && this.pagination.querySelector("a");
      this.label = this.button && this.button.querySelector("span");
      this.mode = this.pagination && this.pagination.dataset.type;
      this.currentPage = Number(page.dataset.currentPage);
      this.pageSize = Number(page.dataset.pageSize);
      this.totalPages = Number(page.dataset.totalPages);
      this.chunkQueue = new RequestQueue(3);
      this.chunks = new Map();
      this.cards = new Map();
      this.productIds = new Set(Array.from(this.grid.children, item => item.dataset.searchProductId));
    }

    url(sectionId, number) {
      const url = new URL(window.location.href);
      url.hash = "";
      url.searchParams.set(this.page.dataset.pageParam || "page", String(number));
      if (sectionId) url.searchParams.set("section_id", sectionId);
      else url.searchParams.delete("section_id");
      return url.href;
    }

    async fetchDocument(url) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error("Search request failed: " + response.status);
        return new DOMParser().parseFromString(await response.text(), "text/html");
      } finally {
        clearTimeout(timeout);
      }
    }

    getChunk(number, priority = false) {
      if (this.chunks.has(number)) {
        if (priority) this.chunkQueue.prioritize(number);
        return this.chunks.get(number);
      }
      const promise = this.chunkQueue.run(number, async () => {
        const doc = await this.fetchDocument(this.url("endpoint--search-index", number));
        const element = doc.querySelector("[data-search-results-index]");
        const data = element && JSON.parse(element.textContent);
        if (!data || data.page !== number || !Number.isInteger(data.pages) || data.pages < number ||
            !Number.isInteger(data.size) || data.size < 1 || !Number.isInteger(data.total) || data.total < 0 ||
            !Array.isArray(data.indices) || new Set(data.indices).size !== data.indices.length ||
            data.indices.some(index => !Number.isInteger(index) || index < (number - 1) * data.size ||
              index >= Math.min(number * data.size, data.total))) {
          throw new Error("Invalid search results index");
        }
        return data;
      }, priority).catch(error => {
        this.chunks.delete(number);
        throw error;
      });
      this.chunks.set(number, promise);
      return promise;
    }

    initializeIndex() {
      if (!this.indexReady) {
        this.indexReady = this.getChunk(1, true).catch(error => {
          this.indexReady = null;
          throw error;
        });
      }
      return this.indexReady;
    }

    async nextPageAfter(number) {
      if (number >= this.totalPages) return null;
      const index = await this.initializeIndex();
      for (let candidate = number + 1; candidate <= this.totalPages; candidate += 1) {
        const start = (candidate - 1) * this.pageSize;
        const end = Math.min(candidate * this.pageSize, index.total);
        if (start >= end) return null;
        const firstChunk = Math.floor(start / index.size) + 1;
        const lastChunk = Math.floor((end - 1) / index.size) + 1;
        const requests = [];
        for (let chunk = firstChunk; chunk <= lastChunk; chunk += 1) requests.push(this.getChunk(chunk, true));
        const chunks = await Promise.all(requests);
        if (chunks.some(chunk => chunk.indices.some(position => position >= start && position < end))) return candidate;
      }
      return null;
    }

    getCards(number) {
      if (!this.cards.has(number)) {
        const promise = this.fetchDocument(this.url(this.page.dataset.section, number)).then(doc => {
          const metadata = doc.querySelector("[data-search-results-page]");
          const items = Array.from(doc.querySelectorAll(".pagination-result"));
          if (!metadata || Number(metadata.dataset.currentPage) !== number ||
              Number(metadata.dataset.pageCount) !== items.length) throw new Error("Invalid search results page");
          return items;
        }).catch(error => {
          this.cards.delete(number);
          throw error;
        });
        this.cards.set(number, promise);
      }
      return this.cards.get(number);
    }

    setTotal(total) {
      document.title = total === 1 ? this.page.dataset.titleOne :
        this.page.dataset.titleMany.replace("__SEARCH_COUNT__", String(total));
      this.countElement.textContent = total + " Results";
      this.countElement.removeAttribute("data-search-results-count-pending");
      this.section.querySelector("[data-search-heading]").hidden = total === 0;
      this.section.querySelector("[data-search-empty]").hidden = total !== 0;
      this.total = total;
      if (total === 0) this.finishPagination();
    }

    updateTotal() {
      if (this.totalPromise) return this.totalPromise;
      this.totalPromise = this.initializeIndex().then(index => {
        const requests = [];
        for (let number = 1; number <= index.pages; number += 1) requests.push(this.getChunk(number));
        return Promise.all(requests);
      }).then(chunks => this.setTotal(chunks.reduce((total, chunk) => total + chunk.indices.length, 0)))
        .catch(error => {
          this.totalPromise = null;
          this.countElement.textContent = "Results";
          console.warn("Could not calculate the search results total", error);
        });
      return this.totalPromise;
    }

    finishPagination() {
      if (this.observer) this.observer.disconnect();
      if (this.pagination) this.pagination.remove();
      this.pagination = null;
    }

    async prepareNext() {
      const from = this.currentPage;
      const next = await this.nextPageAfter(from);
      if (from !== this.currentPage || !this.pagination) return;
      if (next === null) {
        this.finishPagination();
        return;
      }
      this.button.href = this.url(null, next);
      if (!this.loadPromise) this.label.textContent = "Load more products";
      // Counting reads only a small index. Product markup is fetched once and shared with scrolling.
      this.getCards(next).catch(() => {});
    }

    loadMore() {
      if (this.loadPromise || !this.pagination || this.mode === "pages") return this.loadPromise;
      this.failed = false;
      this.pagination.setAttribute("aria-busy", "true");
      this.label.textContent = "Loading…";
      this.loadPromise = (async () => {
        const next = await this.nextPageAfter(this.currentPage);
        if (next === null) {
          this.finishPagination();
          return;
        }
        const items = await this.getCards(next);
        const fragment = document.createDocumentFragment();
        items.forEach(item => {
          if (!this.productIds.has(item.dataset.searchProductId)) {
            this.productIds.add(item.dataset.searchProductId);
            fragment.appendChild(item);
          }
        });
        this.grid.appendChild(fragment);
        this.cards.delete(next);
        this.currentPage = next;
        if (window.Zero.lazyLoad) window.Zero.lazyLoad.update();
        await this.prepareNext();
        if (this.total == null) this.updateTotal();
      })().catch(error => {
        this.failed = true;
        if (this.label) this.label.textContent = "Retry loading products";
        console.warn("Could not load more search products", error);
      }).finally(() => {
        this.loadPromise = null;
        if (this.pagination) {
          this.pagination.removeAttribute("aria-busy");
          if (!this.failed) this.label.textContent = "Load more products";
          // Empty/small batches can leave the trigger visible: continue without requiring another scroll.
          if (!this.failed && this.mode === "infinite") requestAnimationFrame(() => {
            if (this.pagination && this.pagination.getBoundingClientRect().top < window.innerHeight + 600) this.loadMore();
          });
        }
      });
      return this.loadPromise;
    }

    init() {
      if (this.totalPages <= 1) {
        this.setTotal(Number(this.page.dataset.pageCount));
        return Promise.resolve();
      }
      const totalPromise = this.updateTotal();
      if (this.button && this.mode !== "pages") {
        this.button.addEventListener("click", event => {
          event.preventDefault();
          event.stopPropagation();
          this.loadMore();
        });
        this.prepareNext().catch(error => {
          if (!this.loadPromise && this.pagination) {
            this.failed = true;
            this.label.textContent = "Retry loading products";
          }
          console.warn("Could not prefetch search products", error);
        });
        if (this.mode === "infinite" && "IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(entries => {
            if (!this.failed && entries.some(entry => entry.isIntersecting)) this.loadMore();
          }, { rootMargin: "600px 0px" });
          this.observer.observe(this.pagination);
        }
        if (this.grid.children.length === 0) this.loadMore();
      }
      return totalPromise;
    }
  }

  window.Zero = window.Zero || {};
  window.Zero.page = {
    init() {
      if (this.searchResults) return this.searchResults.totalPromise;
      const page = document.querySelector("[data-search-results-page]");
      if (!page) return;
      this.searchResults = new SearchResults(page);
      return this.searchResults.init();
    }
  };
})();
